// components/features/invitations/InvitationRow.tsx
import React, { useState } from "react";
import { MoreVertical, Mail, Check, X } from "lucide-react";
import { Invitation } from "../../../types/features/Invitation";
import TableRow from "../../ui/TableRow";

interface InvitationRowProps {
  invitation: Invitation;
  onAction: (invitation: Invitation) => void;
  onSelect?: (invitation: Invitation, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

export default function InvitationRow({
  invitation,
  onAction,
  onSelect,
  isSelected = false,
  selectable = false,
}: InvitationRowProps) {
  const [showMenu, setShowMenu] = useState(false);

  const getStatusBadge = () => {
    if (invitation.status === "accepted") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <Check className="w-3 h-3" />
          Acceptée
        </span>
      );
    }
    
    if (invitation.is_expired) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <X className="w-3 h-3" />
          Expirée
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
        <Mail className="w-3 h-3" />
        En attente
      </span>
    );
  };

  const getEmailStatusBadge = () => {
    if (invitation.email_status === "sent") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-600">
          <Mail className="w-3 h-3" />
          Envoyé
        </span>
      );
    }
    if (invitation.email_status === "failed") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-red-50 text-red-600">
          <X className="w-3 h-3" />
          Échec
        </span>
      );
    }
    if (invitation.email_status === "accepted") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-50 text-green-600">
          <Check className="w-3 h-3" />
          Accepté
        </span>
      );
    }
    if (invitation.email_status === "expired") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-50 text-gray-500">
          Expiré
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-50 text-gray-500">
        Non envoyé
      </span>
    );
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <TableRow
      item={invitation}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
            {invitation.email.charAt(0).toUpperCase()}
          </div>
          <div>
            <span className="font-medium text-gray-800 block text-sm">{invitation.email}</span>
            <span className="text-xs text-gray-500">{invitation.departement || "Aucun département"}</span>
          </div>
        </div>
      </td>

      <td className="hidden md:table-cell py-3 px-4">
        <span className="text-sm text-gray-600">
          {invitation.entreprise?.name || "-"}
        </span>
      </td>

      <td className="hidden sm:table-cell py-3 px-4">
        <div className="flex flex-col gap-1">
          {getEmailStatusBadge()}
          {invitation.email_retry_count > 0 && (
            <span className="text-xs text-gray-400">
              Tentatives: {invitation.email_retry_count}
            </span>
          )}
        </div>
      </td>

      <td className="hidden lg:table-cell py-3 px-4">
        {getStatusBadge()}
      </td>

      <td className="hidden xl:table-cell py-3 px-4">
        <span className="text-sm text-gray-500">
          {formatDate(invitation.created_at)}
        </span>
      </td>

      <td className="hidden 2xl:table-cell py-3 px-4">
        <span className="text-sm text-gray-500">
          {invitation.expires_at ? formatDate(invitation.expires_at) : "-"}
        </span>
      </td>

      <td className="py-3 px-4">
        <button
          onClick={() => onAction(invitation)}
          className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <MoreVertical className="w-5 h-5 text-gray-500" />
        </button>
      </td>
    </TableRow>
  );
}